import type { Permission } from '../../auth/permissions'
import type { AveryLegacyProposalKind, AveryProposal, AveryProposalRecordResponse } from '@parkify/api-types'

export { API_BASE } from '../../config/apiBase'

export const STORAGE_KEY = 'avery_widget_v1'
export const SEEN_NOTIF_KEY = 'avery_seen_notifications_v1'
export const ACTIONED_KEY = 'avery_actioned_v1'
/**
 * The storage key for Avery's per-person state (transcript, seen and actioned
 * notifications) — namespaced by the View as target while a lens is open, so
 * the lens shows the TARGET's Avery (a fresh one) instead of the operator's own
 * transcript, which can hold other clients' plates and revenue. Reads the lens
 * straight from the AuthContext's persisted key, which is written before the
 * lens state changes, so it can never disagree with the headers a call sends.
 */
const CONSOLE_VIEW_AS_KEY = 'parkify_view_as_user_v1' // AuthContext VIEW_AS_KEY
export function averyKey(base: string): string {
  try {
    // sessionStorage: the lens is scoped to one tab (AuthContext VIEW_AS_KEY).
    const raw = sessionStorage.getItem(CONSOLE_VIEW_AS_KEY)
    const email = raw ? (JSON.parse(raw) as { email?: string }).email : undefined
    return email ? `${base}:viewas:${email.toLowerCase()}` : base
  } catch {
    return base
  }
}
// ISO timestamp: if set and in the future, skip surfacing new optimization
// (severity==='high') notifications. Set when the operator dismisses the last
// remaining optimization card; cleared automatically once the window expires.
export const OPTIM_SNOOZED_UNTIL_KEY = 'avery_optims_snoozed_until_v1'
export const OPTIM_SNOOZE_MS = 24 * 60 * 60 * 1000   // 24 hours
// Parkify staff only — which client the widget's proactive recommendations are
// scoped to. Empty/absent = the all-clients (portfolio) view. Tenant operators
// never set this; the Worker locks them to their own client regardless.
export const VIEW_AS_KEY = 'avery_view_as_client_v1'
// Stable per-card idempotency keys for the money-moving extend proposal. Persisted
// so a Retry — even after a reload — reuses the SAME key, and the Worker replays
// the cached result for a key it has already charged (never a second off-session
// charge). A fresh proposal card mints a fresh key, so legitimately extending the
// same session again still goes through.
export const EXTEND_IDEM_KEY = 'avery_extend_idem_v1'
export const POLL_INTERVAL_MS = 5 * 60 * 1000   // 5 minutes

// Window event other surfaces dispatch to open Avery and (optionally) seed a
// first question — e.g. the Home "Ask Avery" search bar. Kept as a CustomEvent
// to match the app's existing `parkify:navigate` escape hatch, so no shared
// store or prop-threading is needed to reach the globally-mounted widget.
export const ASK_EVENT = 'parkify:avery:ask'

/** Compatibility name for console callers that import the proposal type. */
export type Proposal = AveryProposal
export interface ToolCall { tool: string; input: unknown }
export interface Notification {
  id: string
  kind: string
  severity: 'info' | 'warning' | 'high'
  headline: string
  body: string
  ts: string
  cta?: { label: string; screen?: string }
  context?: Record<string, unknown>
  proposal?: Proposal   // when present, the operator can Approve the change in place
}
/** A screenshot attached to a chat turn. `key` is the R2 object key minted by
 *  POST /api/avery/upload — the bytes live server-side and are re-hydrated by
 *  the Worker on every turn, so the browser never re-uploads. `name` is kept
 *  purely so the chip stays readable after a reload (there is no route that
 *  serves the image back, by design). */
export interface Attachment {
  key: string
  mime: string
  name: string
}

/** An attachment mid-flight in the composer, before the message is sent.
 *  `key`/`mime` land once the upload returns; until then `uploading` is true
 *  and Send stays disabled so a slow upload can't be dropped from the turn. */
export interface StagedAttachment {
  localId: string
  name: string
  /** Local object URL for the thumbnail. Revoked on remove/send — the server
   *  never serves these bytes back, so the preview is tab-lifetime only. */
  previewUrl: string
  uploading: boolean
  key?: string
  mime?: string
}

/** Max screenshots per message. Matches MAX_IMAGES_PER_TURN in the Worker
 *  (avery/index.ts) — the server silently drops extras, so the UI refuses
 *  them up front rather than letting one vanish without explanation. */
export const MAX_ATTACHMENTS = 4

/** Formats the upload route accepts (mirrors ALLOWED_MIME in avery/uploads.ts). */
export const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/webp'

export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  ts: string
  /** Screenshots the operator attached to this turn. Persisted with the
   *  message, so a reload inside the 30d retention window still replays them
   *  to the model. */
  images?: Attachment[]
  proposals?: Proposal[]
  toolTrace?: ToolCall[]
  proposalStatus?: Record<number, ProposalStatus>
  proposalError?: Record<number, string>
  notification?: Notification          // present when message is a proactive notification
}
export type ProposalStatus = 'pending' | 'executing' | 'done' | 'dismissed' | 'error'

/** One row from GET /api/avery/proposals (the server ProposalRecord serialized).
 *  The ops "Avery proposals" inbox consumes this; `proposedBy` distinguishes
 *  bridge-authored rows (see provenance.ts). */
export type ProposalRecordDTO = Pick<AveryProposalRecordResponse,
  'id' | 'correlationId' | 'clientId' | 'kind' | 'payload' | 'reason' |
  'proposedBy' | 'status' | 'createdAt'
>

// Minimal shape of the client records from GET /api/clients — the same source
// ClientAdmin uses. We only need id/name (+ internal for the label badge) to
// populate the staff "Viewing as" picker.
export interface ClientOption { id: string; name: string; internal?: boolean }

export const SUGGESTIONS = [
  'How is today going?',
  'Which zone earned the most this month?',
  'Any open support tickets?',
  'Which permits expire in the next 14 days?',
]

/**
 * Launchpad chips — the teaching surface on an Avery screen nobody has typed
 * into yet.
 *
 * Two deliberate differences from SUGGESTIONS above, both borrowed from the
 * design demo's version of this screen:
 *
 *   1. A chip FILLS the composer instead of sending. The operator sees the
 *      shape of a good ask and can edit it before committing, which is the
 *      whole point — you learn what Avery can be asked by watching it get
 *      asked. Sending immediately teaches nothing.
 *   2. Each chip declares the permission its answer would need, and is not
 *      rendered without it. Offering an action the operator cannot take is
 *      the same UX lie the role gate closed on the server side.
 *
 * Five, not fifteen: the failure here is not "too few options", it is an
 * operator who never tries because the surface looked like work.
 *
 * The demo can hardcode "Kettle Row" because its data is fixed. A real console
 * resolves one instead: `zoneFill` carries a `{zone}` slot filled from the
 * operator's actual busiest lot (useBusiestZoneName), and `fill` is the wording
 * used when no name is available — no session history yet, the week block
 * absent, or the caller lacking the permission that owns a revenue ranking.
 * Both strings are written to stand alone, because BOTH ship: the fallback is
 * the common path for a brand-new tenant, not an error state.
 *
 * A chip with no `zoneFill` is one whose subject genuinely has no zone. Do not
 * invent one to make the set symmetrical — see "Look up a permit" below, which
 * lost a "my main lot" that pointed at nothing in the data model.
 */
export interface LaunchpadChip {
  label: string
  /** Permission the answer needs — checked with the same hasPerm the screens use. */
  perm: Permission
  /** Loaded into the composer verbatim. A complete sentence, never a fragment.
   *  Used as-is when no zone name resolves — so it must read well alone. */
  fill: string
  /** Preferred wording when a busiest-zone name IS known. `{zone}` is replaced
   *  with it. Omit when the question has no zone subject. */
  zoneFill?: string
  /** SVG path data, space-M-separated (see the chip renderer). */
  icon: string
}

export const LAUNCHPAD_CHIPS: LaunchpadChip[] = [
  {
    label: 'Change pricing',
    perm: 'edit.rates',
    fill: 'Raise weekend evening rates at my busiest zone',
    zoneFill: 'Raise weekend evening rates at {zone}',
    icon: 'M12 1v22 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  },
  {
    label: 'Find a plate',
    // `search_sessions`, NOT an enforcement tool — "was this plate paid" is a
    // session lookup, so it takes `view.transactions`. The demo gates the same
    // chip on enforcement because its Avery is scripted and answers nothing.
    perm: 'view.transactions',
    fill: 'Was plate ABC 1234 paid when it was parked yesterday afternoon?',
    icon: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14 M20 20l-4.5-4.5',
  },
  {
    label: 'Issue a validation',
    perm: 'edit.validations',
    fill: 'Give my top validation partner 50 more validations for the rest of the month',
    icon: 'M9 12l2 2 4-4 M12 3l7 4v5c0 4.5-3 8-7 9-4-1-7-4.5-7-9V7z',
  },
  {
    // Backed by propose_add_permit_parker (edit.permits). The fill names a state
    // on purpose: Avery asks for one when it is missing, and the teaching chip
    // should show the complete ask.
    label: 'Add a permit',
    perm: 'edit.permits',
    fill: 'Add plate ABC1234 (Texas), driver Jordan Lee, to the Acme permit',
    icon: 'M12 5v14 M5 12h14',
  },
  {
    // Looking a permit up is backed by `lookup_permit`.
    label: 'Look up a permit',
    // No zone in this one, and no `zoneFill`. It used to say "at my main lot",
    // which reads fine and resolves to NOTHING: there is no primary/main
    // designation anywhere on a Zone, so no code could ever honour it and the
    // busiest-by-revenue lot is not where permits necessarily live. Dropping the
    // phrase entirely matches what `lookup_permit` actually takes (expiringDays).
    perm: 'view.permits',
    fill: 'Which permits expire in the next 30 days?',
    icon: 'M4 5h16a1 1 0 0 1 1 1v3a2 2 0 0 0 0 4v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3a2 2 0 0 0 0-4V6a1 1 0 0 1 1-1 M12 6v12',
  },
  {
    label: 'Explain my payout',
    // There is no `view.payouts` — payouts live under the accounting screen.
    perm: 'view.accounting',
    fill: 'Why was my last payout lower than the one before it?',
    icon: 'M3 6h18v12H3z M3 10h18 M7 15h4',
  },
]

/**
 * The quieter second tier, behind "More…" — same fill-don't-send rule.
 *
 * Every `perm` here and above is the one `TOOL_PERMISSION` (api/src/avery/
 * tools.ts) assigns the tool that would ANSWER the question — not the console
 * screen the topic loosely belongs to. Since the role gate withholds a tool the
 * caller lacks, a chip gated on a different permission than its tool would fill
 * the composer with a question Avery then refuses. Check against that map when
 * adding one, rather than re-deriving it from the topic.
 */
export const LAUNCHPAD_MORE: Array<{ text: string; zoneText?: string; perm: Permission }> = [
  // analyze_zone_revenue
  { text: 'Which zone has the most repeat parkers?', perm: 'view.analytics' },
  // search_sessions
  { text: 'Show me every session longer than 8 hours last month', perm: 'view.transactions' },
  // list_validations
  { text: 'Which tenant hands out the most validations, and what does it cost me?', perm: 'view.validations' },
  // propose_create_zone
  { text: 'Set up a new zone and walk me through go-live', perm: 'edit.zones' },
  // Today's activity is sessions and revenue, so it reads through the same
  // permission search_sessions takes — not `view.home`, which every role holds
  // and would therefore gate nothing.
  { text: 'How is today going?', perm: 'view.transactions' },
  // analyze_zone_revenue
  {
    text: 'What would raising my busiest zone by $1 an hour have earned last week?',
    zoneText: 'What would raising {zone} by $1 an hour have earned last week?',
    perm: 'view.analytics',
  },
]

/**
 * Resolve a chip's composer text against the caller's busiest-zone name.
 *
 * One helper for both tiers so the `{zone}` contract can't diverge between
 * them. `null` zone (or an entry with no zone variant) yields the plain `fill`,
 * which is why every `fill` has to be a complete sentence on its own.
 */
export function chipText(
  entry: { fill?: string; text?: string; zoneFill?: string; zoneText?: string },
  zoneName: string | null,
): string {
  const plain = entry.fill ?? entry.text ?? ''
  const templated = entry.zoneFill ?? entry.zoneText
  if (!zoneName || !templated) return plain
  return templated.replace('{zone}', zoneName)
}

export const ASK_SUGGESTIONS = [
  'Search a plate, zone, or transaction…',
  'How is today going?',
  'Which zone earned the most this month?',
  'Any open support tickets?',
  "Show me today's refunds",
  'Which permits expire soon?',
]
export const ASK_FADE_MS = 420
export const ASK_HOLD_MS = 2800

type ProposalLabel = { title: string; verb: string; color: string }

// Bespoke kinds only. Route-backed kinds take their labels from the registry
// (@parkify/api-types/avery-actions) through the lazily loaded routeActions.ts,
// so the registry stays out of the always-loaded chunk.
export const PROPOSAL_LABELS: Readonly<Record<AveryLegacyProposalKind, ProposalLabel>> = {
  refund:             { title: 'Issue refund',      verb: 'Refund',       color: '#F16521' },
  resolve_ticket:     { title: 'Resolve ticket',    verb: 'Resolve',      color: '#62c584' },
  update_rates_base:  { title: 'Update base rate',  verb: 'Update rate',  color: 'var(--parkify-accent)' },
  update_rates_event: { title: 'Update event rate', verb: 'Update rate',  color: 'var(--parkify-accent)' },
  add_zone_photo:     { title: 'Add zone photo',    verb: 'Add photo',    color: 'var(--parkify-accent)' },
  add_event:          { title: 'Add event rate',    verb: 'Add event',    color: 'var(--parkify-accent)' },
  add_time_of_day_rate: { title: 'Add time-of-day surge', verb: 'Add surge', color: 'var(--parkify-accent)' },
  remove_time_of_day_rate: { title: 'Remove time-of-day surge', verb: 'Undo surge', color: '#c0392b' },
  cancel_subscription: { title: 'Cancel subscription', verb: 'Cancel at period end', color: '#b91c1c' },
  change_subscription_plan: { title: 'Change plan', verb: 'Change plan', color: 'var(--parkify-accent)' },
  set_base_rate:          { title: 'Set base rate',     verb: 'Set rate',     color: 'var(--parkify-accent)' },
  set_graduated_schedule: { title: 'Set graduated rate', verb: 'Set schedule', color: 'var(--parkify-accent)' },
  set_min_duration:       { title: 'Set minimum duration', verb: 'Set minimum', color: 'var(--parkify-accent)' },
  set_duration_ladder:    { title: 'Set checkout durations', verb: 'Set durations', color: 'var(--parkify-accent)' },
  set_rate_product:       { title: 'Set rate windows',    verb: 'Set rates',    color: 'var(--parkify-accent)' },
  set_daily_max:          { title: 'Set daily max',       verb: 'Set daily max', color: 'var(--parkify-accent)' },
  set_monthly_plan:       { title: 'Set monthly plan',    verb: 'Save plan',    color: 'var(--parkify-accent)' },
  set_day_of_week_rate:   { title: 'Set day-of-week rate', verb: 'Set day rate', color: 'var(--parkify-accent)' },
  set_pass:               { title: 'Set pass',            verb: 'Save pass',    color: 'var(--parkify-accent)' },
  set_demand_tiers:       { title: 'Set dynamic pricing', verb: 'Set bands',    color: 'var(--parkify-accent)' },
  update_zone_fees:       { title: 'Update zone fees',   verb: 'Update fees',  color: 'var(--parkify-accent)' },
  extend_session:         { title: 'Add time to session', verb: 'Add time',    color: '#F16521' },
  reopen_ticket:          { title: 'Reopen ticket',      verb: 'Reopen',       color: '#F16521' },
  escalate_ticket:        { title: 'Escalate ticket',    verb: 'Escalate',     color: '#b91c1c' },
  resync_plate:           { title: 'Resync plate',       verb: 'Resync',       color: 'var(--parkify-accent)' },
  update_zone_config:     { title: 'Update zone details', verb: 'Update details', color: 'var(--parkify-accent)' },
  create_zone:            { title: 'Create zone',         verb: 'Create zone',   color: 'var(--parkify-accent)' },
  upsert_validation:      { title: 'Validation code',     verb: 'Save code',     color: 'var(--parkify-accent)' },
  delete_validation:      { title: 'Delete validation',   verb: 'Delete code',   color: '#c0392b' },
  create_client:          { title: 'Create client',       verb: 'Create client', color: 'var(--parkify-accent)' },
  invite_team_member:     { title: 'Invite team member',  verb: 'Send invite',   color: 'var(--parkify-accent)' },
  send_payout_link:       { title: 'Send payout setup link', verb: 'Send link',  color: 'var(--parkify-accent)' },
  create_facility:        { title: 'Create location',      verb: 'Create location', color: 'var(--parkify-accent)' },
  // Amber, not accent: this one moves PARKIFY's cut, so the card should not
  // look like the routine config saves around it.
  update_fee_model:       { title: 'Set fee model',        verb: 'Set fee model', color: '#b45309' },
  // Same amber treatment as update_fee_model — platformFeeBps is PARKIFY's cut.
  set_contract_terms:     { title: 'Contract terms',        verb: 'Apply terms',  color: '#b45309' },
}

/** A kind with bespoke console code (its own executeProposal branch and label). */
export function isLegacyProposalKind(kind: unknown): kind is AveryLegacyProposalKind {
  return typeof kind === 'string' && Object.prototype.hasOwnProperty.call(PROPOSAL_LABELS, kind)
}
